import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useStreak from '../hooks/useStreak';
import ModalFrame from './ui/ModalFrame';
import SheetFrame from './ui/SheetFrame';
import Iconify from './Iconify';
import ProgressRing from './ProgressRing';
import SplitMeter from './SplitMeter';
import TierLadder from './TierLadder';
import ArchetypeBlock from './ArchetypeBlock';
import StreakLounge from './StreakLounge';
import { DOMAIN } from '../redux/constants';
import { formatCountdown, currentTierNumber, RECOVERY_VOTES_GOAL } from '../utils/streak';
import { DUR, EASE_OUT } from '../utils/motion';
import { LABEL_MICRO, LABEL_SM, NUMERAL_HERO, NUMERAL_LG, TITLE_SECTION } from '../utils/typeScale';
import { RED, DARK_TEXT } from '../utils/brandColors';

StreakModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  streakData: PropTypes.object,
  onStreakChange: PropTypes.func,
};

export default function StreakModal({ open, onClose, streakData: initialData, onStreakChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.userLogin);
  const [data, setData] = useState(initialData || null);
  const [view, setView] = useState('progress');
  const [now, setNow] = useState(Date.now());

  // Refresh through the SHARED streak store on open (this used to be a
  // fourth private fetcher of the same endpoint, so the modal could show
  // different numbers than the pill that opened it), and mirror the store
  // into local state so recovery-claim responses can still overwrite.
  const { streak: sharedStreak, refresh: refreshShared } = useStreak();
  useEffect(() => {
    if (open) {
      refreshShared();
    } else {
      setView('progress');
    }
  }, [open, refreshShared]);
  useEffect(() => {
    if (sharedStreak) setData(sharedStreak);
  }, [sharedStreak]);

  const currentStreak = data?.current_streak ?? 0;
  const longestStreak = data?.longest_streak ?? 0;
  const dailyGoal = data?.daily_vote_goal ?? 3;
  // Clamp: `daily_vote_count` is a raw running total and legitimately exceeds
  // the goal (vote 110 times in a day and the server reports 110). The ring
  // reads as "progress toward today's goal", so an unclamped value renders
  // "110/3" and overflows its own geometry.
  const dailyCount = Math.min(data?.daily_vote_count ?? 0, dailyGoal);
  const shields = data?.streak_shields ?? 0;
  const maxShields = data?.max_shields ?? 2;
  const recoveryActive = data?.recovery_active ?? false;
  const recoveryExpiresAt = data?.recovery_expires_at;
  const recoveryVotes = data?.recovery_votes_count ?? 0;
  const recoveryGoalRaw = data?.recovery_votes_goal ?? RECOVERY_VOTES_GOAL;
  // ?? doesn't catch a literal 0 from the server; 0/0 fed NaN into the meter.
  const recoveryGoal = recoveryGoalRaw > 0 ? recoveryGoalRaw : RECOVERY_VOTES_GOAL;
  const oracleXp = data?.oracle_xp ?? 0;
  const totalVotes = data?.total_votes ?? 0;

  // The recovery countdown must never mount on the two fallback response
  // shapes — the anonymous branch and the no-VoteStreak-row branch omit
  // recovery_expires_at entirely, and `new Date(undefined)` is an Invalid Date
  // ticker. Gate on both flags.
  const showCountdown = recoveryActive && Boolean(recoveryExpiresAt);
  useEffect(() => {
    if (!open || !showCountdown) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open, showCountdown]);

  const countdown = showCountdown ? formatCountdown(recoveryExpiresAt, now) : null;
  const tier2Unlocked = currentTierNumber(data) >= 2;

  const handleClaimRecovery = useCallback(() => {
    if (!userInfo?.access) return;
    axios
      .post(
        `${DOMAIN}/api/v1/engagement/streak/recovery/claim/`,
        {},
        { headers: { Authorization: `Bearer ${userInfo.access}` } }
      )
      .then((res) => {
        if (res.data?.streak) setData(res.data.streak);
        if (onStreakChange) onStreakChange();
        // Still short of the goal — send them somewhere they can actually vote.
        if (res.data?.status === 'in_progress') {
          onClose();
          navigate('/dashboard/app');
        }
      })
      .catch(() => {});
  }, [userInfo, onStreakChange, onClose, navigate]);

  const goVote = () => {
    onClose();
    navigate('/dashboard/app');
  };

  // No close button here — ModalFrame and SheetFrame each render their own,
  // and a second one in the content produced two stacked X glyphs.
  const header = (
    // baseline, not flex-start: the hero and LG numerals are different sizes,
    // so top-aligning them (which the old `pt: 1` below tried to correct by
    // eyeball) left the digits and their labels floating at unrelated heights.
    <Stack direction="row" spacing={3} alignItems="baseline" sx={{ mb: 3 }}>
      <Box>
        <Typography sx={{ ...NUMERAL_HERO, color: 'text.primary' }}>{currentStreak}</Typography>
        <Typography sx={{ ...LABEL_MICRO, mt: 0.5 }}>Day streak</Typography>
      </Box>
      {/* Oracle XP is Tier-2-gated and starts at 0 for everyone — a zero here
          is noise, not information. It appears once the user has earned any. */}
      {oracleXp > 0 ? (
        <Box>
          <Typography sx={{ ...NUMERAL_LG, color: 'text.primary' }}>{oracleXp}</Typography>
          <Typography sx={{ ...LABEL_MICRO, mt: 0.5 }}>Oracle XP</Typography>
        </Box>
      ) : null}
    </Stack>
  );

  // Progressive disclosure: a fresh account used to open this modal onto five
  // zeros (streak, XP, best, votes, 0/3 ring) with nothing saying which one
  // mattered. Each stat now earns its place: Best only when it beats the
  // current run (equal is redundant), Total votes once any exist, Shields only
  // while holding one. When nothing qualifies the whole row disappears.
  const showBest = longestStreak > 0 && longestStreak > currentStreak;
  const showTotalVotes = totalVotes > 0;
  const showShields = shields > 0;
  const statRow =
    showBest || showTotalVotes || showShields ? (
      // flex-end so the shields' 24px icon row and the 20px numerals sit on one
      // visual floor, which in turn puts all the labels beneath them on one
      // line instead of a stagger.
      <Stack direction="row" spacing={3} alignItems="flex-end" sx={{ mb: 3 }}>
        {showBest ? (
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {longestStreak}
            </Typography>
            <Typography sx={LABEL_MICRO}>Best</Typography>
          </Box>
        ) : null}
        {showTotalVotes ? (
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {totalVotes}
            </Typography>
            <Typography sx={LABEL_MICRO}>Total votes</Typography>
          </Box>
        ) : null}
        {showShields ? (
          <Box>
            {/* Shields render as glyphs, not "1 / 2" — you can see at a glance how
                many saves you are holding. */}
            <Stack direction="row" spacing={0.5} sx={{ height: 24, alignItems: 'center' }}>
              {Array.from({ length: maxShields }).map((_, i) => (
                <Iconify
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  icon={i < shields ? 'eva:shield-fill' : 'eva:shield-outline'}
                  width={18}
                  height={18}
                  sx={{ color: i < shields ? RED : 'text.disabled' }}
                />
              ))}
            </Stack>
            <Typography sx={LABEL_MICRO}>Shields</Typography>
          </Box>
        ) : null}
      </Stack>
    ) : null;

  const dailyBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 3,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <ProgressRing
        value={dailyCount}
        goal={dailyGoal}
        size={56}
        strokeWidth={5}
        color={RED}
        trackColor={alpha(RED, 0.15)}
        reducedMotion={prefersReducedMotion}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
          {dailyCount}/{dailyGoal}
        </Typography>
      </ProgressRing>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={TITLE_SECTION}>
          {dailyCount >= dailyGoal ? "Today's goal is done" : 'Keep the streak alive'}
        </Typography>
        <Typography sx={{ ...LABEL_SM, mt: 0.25 }}>
          {dailyCount >= dailyGoal
            ? 'Your streak is safe until tomorrow.'
            : `Vote ${dailyGoal - dailyCount} more time${dailyGoal - dailyCount === 1 ? '' : 's'} today.`}
        </Typography>
      </Box>
      {dailyCount < dailyGoal ? (
        <Button
          onClick={goVote}
          variant="contained"
          size="small"
          sx={{ borderRadius: '10px', fontWeight: 800, textTransform: 'none', flexShrink: 0 }}
        >
          Vote
        </Button>
      ) : null}
    </Box>
  );

  const recoveryBlock = recoveryActive ? (
    <Box sx={{ borderRadius: '12px', p: 2.5, mb: 3, bgcolor: RED, color: '#fff' }}>
      <Typography sx={{ ...LABEL_MICRO, color: 'rgba(255,255,255,0.7)' }}>Recovery quest</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em', mt: 0.5 }}>
        Win your streak back
      </Typography>
      {countdown ? (
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1.5 }}>
          <Typography
            sx={{ fontSize: 32, fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
          >
            {countdown}
          </Typography>
          <Typography sx={{ ...LABEL_MICRO, color: 'rgba(255,255,255,0.7)' }}>Left</Typography>
        </Stack>
      ) : null}
      <Box sx={{ mt: 2 }}>
        <SplitMeter
          percentA={Math.min(100, (recoveryVotes / recoveryGoal) * 100)}
          height={4}
          fillColor="#fff"
          trackColor="rgba(255,255,255,0.28)"
          reducedMotion={prefersReducedMotion}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
          <Typography sx={{ ...LABEL_MICRO, color: 'rgba(255,255,255,0.7)' }}>
            {recoveryVotes} of {recoveryGoal} votes
          </Typography>
          <Button
            onClick={handleClaimRecovery}
            size="small"
            sx={{
              bgcolor: '#fff',
              color: DARK_TEXT,
              borderRadius: '10px',
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
            }}
          >
            {recoveryVotes >= recoveryGoal ? 'Claim streak' : 'Continue quest'}
          </Button>
        </Stack>
      </Box>
    </Box>
  ) : null;

  // Action first: the recovery quest (when live) and today's goal lead the
  // view; the identity numbers follow. What the user should DO outranks what
  // they have accumulated.
  const progressView = (
    <>
      {recoveryBlock}
      {dailyBlock}
      {header}
      {statRow}
      <Box sx={{ mb: 3 }}>
        <ArchetypeBlock archetype={data?.archetype} metrics={data?.metrics} />
      </Box>
      <Typography variant="h6" sx={{ ...TITLE_SECTION, mb: 0.5 }}>
        Tiers
      </Typography>
      <TierLadder streak={data} />
      {tier2Unlocked ? (
        <Button
          fullWidth
          onClick={() => setView('lounge')}
          endIcon={<Iconify icon="eva:arrow-ios-forward-fill" width={18} height={18} />}
          sx={{
            mt: 2,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            py: 1.25,
            fontWeight: 800,
            textTransform: 'none',
            color: 'text.primary',
            justifyContent: 'space-between',
          }}
        >
          Enter the Streak Lounge
        </Button>
      ) : null}
    </>
  );

  const loungeView = (
    <>
      {header}
      <Button
        onClick={() => setView('progress')}
        startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={18} height={18} />}
        sx={{ mb: 2, fontWeight: 700, textTransform: 'none', color: 'text.secondary' }}
      >
        Back
      </Button>
      <StreakLounge onLocked={() => setView('progress')} onNavigate={onClose} />
    </>
  );

  const content = (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        animation: prefersReducedMotion ? 'none' : `sheetIn ${DUR.ui}ms ${EASE_OUT} both`,
        '@keyframes sheetIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      {view === 'progress' ? progressView : loungeView}
    </Box>
  );

  if (isMobile) {
    return (
      <SheetFrame open={open} onClose={onClose}>
        {content}
      </SheetFrame>
    );
  }

  return (
    <ModalFrame open={open} onClose={onClose} maxWidth="sm">
      {content}
    </ModalFrame>
  );
}
